import { downloadFullTable } from "../../../../services/apiServices";

/**
 * Reads the rendered DOM of a table (via ref) and downloads it as a CSV file.
 * Used by the markdown <table> renderer where we don't have the original CSV
 * string — only the rendered cells.
 */
export const downloadCSV = (tableRef) => {
  if (!tableRef?.current) return;

  let csvContent = "";
  const rows = tableRef.current.querySelectorAll("tr");

  rows.forEach((row) => {
    const cols = row.querySelectorAll("td, th");
    const rowContent = Array.from(cols)
      .map((col) => `"${col.innerText}"`)
      .join(",");
    csvContent += rowContent + "\n";
  });

  triggerCSVDownload(csvContent, "table.csv");
};

/**
 * Downloads the CSV the user is currently looking at. For truncated samples
 * the filename reflects the sample size; otherwise it uses the full row count.
 */
export const downloadVisibleTable = (csvContent, metadata) => {
  const isTruncated =
    metadata && metadata.full_size > metadata.sample_size;
  const filename = isTruncated
    ? `table_sample_${metadata.sample_size}_rows.csv`
    : `table_${metadata ? metadata.full_size : "data"}_rows.csv`;

  triggerCSVDownload(csvContent, filename);
};

/**
 * Pulls the full dataset from the backend (by table_id) and downloads it.
 *
 * Falls back to `downloadVisibleTable` when the rendered block already
 * contains the full dataset — there's no QueryResult row to fetch in that
 * case.
 */
export const downloadFullDataset = async (metadata, csvContent, clientId) => {
  const isTruncated =
    metadata && metadata.full_size > metadata.sample_size;
  if (!isTruncated || !metadata?.table_id) {
    downloadVisibleTable(csvContent, metadata);
    return;
  }

  try {
    const { blob, filename } = await downloadFullTable(
      metadata.table_id,
      clientId,
    );

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading full dataset:", error);
    alert("Error downloading full dataset. Please try again.");
  }
};

const triggerCSVDownload = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
